import React, { useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import { Trophy, Star, Zap, Globe, Users, Lightbulb } from "lucide-react";
import "./AwardsCarousel.scss";

import aw1   from "../assets/awards/aw1.webp";
import aw2   from "../assets/awards/aw2.webp";
import aw3   from "../assets/awards/aw3.webp";
import aw4   from "../assets/awards/aw4.webp";
import aw5   from "../assets/awards/aw5.webp";
import aw6   from "../assets/awards/aw6.webp";
import aws   from "../assets/awards/aws.webp";
import aws10 from "../assets/awards/aws10.webp";
import aws11 from "../assets/awards/aws11.webp";
import mouEmrs from "../assets/mou/mou emrss.jpeg";
import mouNielitSigning from "../assets/mou/neilet.png";

const AWARDS = [
  {
    id: 1, image: aw1, featured: true,
    icon: Trophy, label: "Featured Award",
    title: "Best Emerging Rural Skill Learning Platform",
    org: "Indian Education Awards", year: "2024",
    desc: "Recognized for our outstanding contribution to rural education and skill development through innovative technology and impactful learning solutions.",
  },
  {
    id: 2, image: aws,
    icon: Zap, label: "National Recognition",
    title: "AI × Education Thinktank",
    org: "MeitY · IIT Delhi", year: "2024",
    desc: "Selected among India's top EdTech innovators at the prestigious MeitY–IIT Delhi AI Education Thinktank.",
  },
  {
    id: 3, image: aw3,
    icon: Star, label: "Excellence Award",
    title: "Bharatiya Excellence Award",
    org: "Bharatiya Excellence Awards", year: "2024",
    desc: "Honored for exceptional social impact and excellence in education technology across India.",
  },
  {
    id: 4, image: aw6,
    icon: Globe, label: "National Award",
    title: "Rashtriya Navoday Gaurav Award",
    org: "National Recognition · Jaipur", year: "2024",
    desc: "Awarded at national level for transformative contribution to youth education and skill development.",
  },
  {
    id: 5, image: aws10,
    icon: Lightbulb, label: "Innovation Award",
    title: "AI × Education Thinktank — IIT Delhi",
    org: "FITT · IIT Delhi", year: "2024",
    desc: "Represented Cograd at the prestigious AI x Education Thinktank held at IIT Delhi.",
  },
  {
    id: 6, image: aw2,
    icon: Users, label: "Entrepreneurs Award",
    title: "Entrepreneurs India Award",
    org: "Entrepreneurs India Summit", year: "2023",
    desc: "Recognized for innovative approaches to education and rural empowerment at the national stage.",
  },
  {
    id: 7, image: aws11,
    icon: Zap, label: "Incubation Recognition",
    title: "NASSCOM thingQbator Cohort",
    org: "NASSCOM Foundation", year: "2024",
    desc: "Selected into NASSCOM Foundation's thingQbator incubation program for turning bold ideas into impactful prototypes.",
  },
  {
    id: 8, image: aw4,
    icon: Star, label: "Certificate of Recognition",
    title: "Certificate of Recognition",
    org: "Dream Advance", year: "2023",
    desc: "Felicitated for outstanding contribution and mentorship in advancing skill-based learning in the region.",
  },
  {
    id: 9, image: aw5,
    icon: Trophy, label: "Felicitation",
    title: "Institutional Felicitation",
    org: "Partner Institution", year: "2023",
    desc: "Honored with a memento for exceptional collaboration and impact in education and skill development.",
  },
  {
    id: 10, image: mouEmrs,
    icon: Globe, label: "Government Partnership",
    title: "MoU with EMRS · Ministry of Tribal Affairs",
    org: "Ministry of Tribal Affairs", year: "2024",
    desc: "Signed a landmark MoU with EMRS to bring AI-powered learning and future-ready skills to tribal schools across India.",
  },
  {
    id: 11, image: mouNielitSigning,
    icon: Zap, label: "National Partnership",
    title: "MoU with NIELIT — Industry–Academia Meet 2026",
    org: "NIELIT · SCOPE, New Delhi", year: "2026",
    desc: "Partnered with NIELIT at the Industry–Academia Meet 2026 to advance India's digital learning ecosystem.",
  },
];

export default function AwardsCarousel() {
  const [active, setActive] = useState(0);

  return (
    <section className="aw-wrap">

      {/* heading stays in constrained container */}
      <div className="aw-inner">
        <motion.div
          className="aw__header"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="aw__chip">
            <Trophy size={12} strokeWidth={2.5} /> CELEBRATING EXCELLENCE
          </div>
          <h2 className="aw__h2">Awards and <span>Recognition</span></h2>
          <p className="aw__sub">
            Honoring our commitment to innovation, impact, and excellence<br />
            in education and skill development.
          </p>
          <div className="aw__laurel aw__laurel--left"  aria-hidden="true" />
          <div className="aw__laurel aw__laurel--right" aria-hidden="true" />
        </motion.div>
      </div>

      {/* coverflow is full-width — outside aw-inner so centeredSlides = viewport center */}
      <motion.div
        className="aw__cf-wrap"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <Swiper
          modules={[EffectCoverflow, Autoplay, Pagination]}
          effect="coverflow"
          grabCursor
          centeredSlides
          slidesPerView="auto"
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 180,
            modifier: 2.4,
            slideShadows: false,
          }}
          autoplay={{ delay: 2800, disableOnInteraction: false }}
          loop
          pagination={{ clickable: true, el: ".aw__cf-dots" }}
          onSlideChange={(s) => setActive(s.realIndex)}
          className="aw__cf-swiper"
        >
          {AWARDS.map((award) => (
            <SwiperSlide key={award.id} className="aw__cf-slide">
              <div className="aw__cf-img-wrap">
                <img src={award.image} alt={award.title} loading="lazy" />
                <div className="aw__cf-overlay" />
                <span className="aw__cf-bottom-label">{award.label}</span>
                {award.featured && (
                  <span className="aw__cf-feat-badge">
                    <Trophy size={10} strokeWidth={2.5} /> Best Award
                  </span>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="aw__cf-dots" />
      </motion.div>

    </section>
  );
}
